import { encodeBase64 } from 'utils/base64';
import { urls } from 'config';
import {
  isEmailValid,
  storeTokens,
  isAccessTokenExpired,
  updateAccessToken,
} from 'utils';
import { SubmissionError } from 'redux-form/lib/SubmissionError';
import { createSelector } from 'reselect';
import { env_externalservices } from 'env';
import { getAuthUserCountry } from 'utils/getAuthUserCountry';
import { setTalkMeClientInfo } from 'utils/talkMe';
import { commonGet, commonPost, CUSTOM_ERROR_CODES } from './api';
import { saveData } from './utils';
import shareInFlight from './inFlight';

export const auth = {
  SET_LOGOUT: 'AUTH_SET_LOGOUT',
  SET_CURRENT_USER: 'AUTH_SET_CURRENT_USER',
  SET_CURRENT_CUSTOMER: 'AUTH_SET_CURRENT_CUSTOMER',
  SET_CURRENT_ADVISOR: 'AUTH_SET_CURRENT_ADVISOR',
};

// reducer
const defaultState = {
  currentUser: {},
  currentCustomer: {},
  currentAdvisor: {},
};

export default (state = defaultState, action) => {
  switch (action.type) {
    case auth.SET_CURRENT_USER: {
      if (env_externalservices === 'true') {
        setTalkMeClientInfo(action.payload);
      }

      return { ...state, currentUser: action.payload };
    }
    case auth.SET_CURRENT_CUSTOMER:
      return { ...state, currentCustomer: action.payload };
    case auth.SET_CURRENT_ADVISOR:
      return { ...state, currentAdvisor: action.payload };
    case auth.SET_LOGOUT:
      return defaultState;
    default:
      return state;
  }
};

// actions
export function setLogout() {
  return {
    type: auth.SET_LOGOUT,
  };
}

const commonLogin =
  ({ url, headers }) =>
  async (dispatch) => {
    const data = await commonPost({
      url,
      headers,
      errorMessage: 'Ошибка авторизации',
    });
    await storeTokens(data.data, dispatch);
    return data;
  };

// selector
export const getCurrentUserSelector = (state) => state.auth.currentUser;

export const getAuthUserCountrySelector = createSelector(
  [(state) => state.auth.currentCustomer, (state) => state.auth.currentAdvisor],
  getAuthUserCountry
);

// Request JWT
export function login({ login: loginValue, password }) {
  if (!isEmailValid(loginValue))
    throw new SubmissionError({ _error: 'Введен некорректный email' });
  if (!password)
    throw new SubmissionError({ _error: 'Введен некорректный пароль' });

  const authStr = `Basic ${encodeBase64(
    unescape(encodeURIComponent(`${loginValue}:${password}`))
  )}`;

  return commonLogin({
    url: urls().login,
    headers: { Authorization: authStr },
  });
}

export function bayerLogin() {
  return commonLogin({ url: urls().bayertoken, headers: {} });
}

export const getAuthHeader = (token) => ({ Authorization: `Bearer ${token}` });

export const postRefreshToken = async () => {
  const refreshToken = localStorage.getItem('JWT_REFRESH');
  if (refreshToken) {
    const { data } = await commonPost({
      url: urls().refreshToken,
      headers: getAuthHeader(refreshToken),
      errorMessage: 'Ошибка',
    });
    updateAccessToken(data);
    return Promise.resolve(data.access_token);
  }
  return Promise.reject(new Error(CUSTOM_ERROR_CODES.REFRESH_TOKEN_NOT_FOUND));
};

export const checkToken = () =>
  isAccessTokenExpired() ? null : localStorage.getItem('JWT_ACCESS');

export function getAuthUsers() {
  return commonGet({ url: urls().authUsers });
}

// Share only an in-flight read within the same store/session. Completed reads
// are never cached: returning to a screen still refreshes the profile.
const currentUserRequests = new WeakMap();

export function getCurrentUser(id, { force = false } = {}) {
  return (dispatch) => {
    const url = `${urls().authUsers}/${id}`;
    const session = localStorage.getItem('JWT_REFRESH');
    const pending = currentUserRequests.get(dispatch);
    if (!force && pending?.url === url && pending.session === session) {
      return pending.promise;
    }
    const request = { url, session };
    currentUserRequests.set(dispatch, request);
    request.promise = saveData(
      url,
      auth.SET_CURRENT_USER
    )((action) => {
      if (
        currentUserRequests.get(dispatch) === request &&
        localStorage.getItem('JWT_REFRESH') === session
      ) {
        return dispatch(action);
      }
      return action;
    }).finally(() => {
      if (currentUserRequests.get(dispatch) === request) {
        currentUserRequests.delete(dispatch);
      }
    });
    return request.promise;
  };
}

// The header, works and unattached tracks read the customer when a field page
// opens: they share the request in flight of the same session.
export function getCurrentCustomer(id) {
  return (dispatch) => {
    const url = `${urls().customers}/${id}`;
    const session = localStorage.getItem('JWT_REFRESH');
    return shareInFlight(dispatch, `${url} ${session}`, () =>
      saveData(url, auth.SET_CURRENT_CUSTOMER)(dispatch)
    );
  };
}

export function getCurrentAdvisor(id) {
  return saveData(`${urls().advisors}/${id}`, auth.SET_CURRENT_ADVISOR);
}

// selectors
export const getAuthCurrentUserSelector = (state) => state.auth.currentUser;

export const getAuthCurrentCustomerSelector = (state) =>
  state.auth.currentCustomer;
